import axios, { type AxiosError, type AxiosInstance } from "axios";
import toast from "react-hot-toast";
import { PermissionDeniedError, refreshAccessToken } from "../../shared/auth";
import { getFieldErrorMessage } from "../helpers/error";
import type { RequestError } from "../types";

const isUnauthorized = (error: AxiosError<RequestError>) => {
  const details = error.response?.data;

  return (
    error.response?.status === 401 ||
    details?.errors?.some(({ msg }) => msg.includes("Signature has expired")) ||
    details?.python_exc?.includes("Signature has expired")
  );
};

const showRequestError = (error: AxiosError<RequestError>) => {
  if (error.response?.data.type === "logic") return;

  const details = error.response?.data;
  const message = Array.isArray(details?.errors)
    ? getFieldErrorMessage(error)
    : details?.python_exc || error.message;
  if (message) toast.error(message);
};

export function responseErrorHandlerBuilder(instance: AxiosInstance) {
  return async (unknownError: unknown) => {
    // A cancellation is a deliberate act by the caller, not a fault. Kiosk views
    // abort in-flight requests when they unmount, when the customer dismisses a
    // dialog, and when an operation times out, so surfacing "canceled" as a
    // toast would show a customer an error for something they just asked for.
    // Still reject, so callers keep deciding what a cancelled request means.
    if (axios.isCancel(unknownError)) return Promise.reject(unknownError);

    if (!axios.isAxiosError<RequestError>(unknownError)) {
      if (unknownError instanceof Error) toast.error("Unexpected error");
      return Promise.reject(unknownError);
    }

    const error = unknownError;
    const originalRequest = error.config;

    // A 403 is an authorization decision, not an expired authentication token.
    // Never refresh or replay it; consumers use this typed failure to fail closed.
    if (error.response?.status === 403 && !originalRequest?.authRecovery?.skip) {
      return Promise.reject(new PermissionDeniedError(error));
    }

    if (
      isUnauthorized(error) &&
      originalRequest &&
      !originalRequest._authRetry &&
      !originalRequest.authRecovery?.skip
    ) {
      originalRequest._authRetry = true;

      try {
        await refreshAccessToken({
          storeKey: originalRequest.authRecovery?.storeKey,
          reason: originalRequest.authRecovery?.reason ?? "http-401",
        });

        // The request interceptor reads the session again, so this replay always
        // receives the access token written by the completed refresh request.
        return instance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    if (!originalRequest?.suppressErrorToast) showRequestError(error);
    return Promise.reject(error);
  };
}
